import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';

const RegistrationTable = ({
  registrations,
  selectedRegistrations,
  onSelectRegistration,
  onSelectAll,
  onViewDetails,
  onApprove,
  onReject,
  onRequestInfo,
  viewMode
}) => {
  const getStatusColor = (status) => {
    const colors = {
      'Pending': 'bg-warning/10 text-warning border-warning/20',
      'Under Review': 'bg-accent/10 text-accent border-accent/20',
      'Verified': 'bg-success/10 text-success border-success/20',
      'Rejected': 'bg-error/10 text-error border-error/20',
      'Approved': 'bg-primary/10 text-primary border-primary/20'
    };
    return colors?.[status] || 'bg-muted text-muted-foreground';
  };

  const getTypeIcon = (type) => {
    const icons = {
      'Student': 'GraduationCap',
      'Employer': 'Building2',
      'Institution': 'School'
    };
    return icons?.[type] || 'User';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      'high': 'text-error',
      'normal': 'text-muted-foreground',
      'low': 'text-success'
    };
    return colors?.[priority] || 'text-muted-foreground';
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  if (registrations?.length === 0) {
    return (
      <div className="text-center py-12">
        <Icon name="Inbox" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-4" />
        <p className="text-lg font-medium text-muted-foreground">No registrations found</p>
        <p className="text-sm text-muted-foreground mt-1">Try adjusting your filters</p>
      </div>
    );
  }

  if (viewMode === 'card') {
    return (
      <div className="space-y-4">
        {registrations?.map((registration) => (
          <div
            key={registration?.id}
            className={`bg-card border border-border rounded-lg p-4 hover:shadow-warm-md transition-smooth ${
              selectedRegistrations?.includes(registration?.id) ? 'ring-2 ring-primary' : ''
            }`}
          >
            <div className="flex items-start gap-4">
              <Checkbox
                id={`reg-${registration?.id}`}
                checked={selectedRegistrations?.includes(registration?.id)}
                onChange={() => onSelectRegistration(registration?.id)}
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <Icon name={getTypeIcon(registration?.registrationType)} size={18} color="var(--color-primary)" />
                      <h3 className="text-base md:text-lg font-semibold text-card-foreground line-clamp-1">
                        {registration?.applicantName}
                      </h3>
                      {registration?.priority === 'high' && (
                        <Icon name="AlertCircle" size={16} className={getPriorityColor(registration?.priority)} />
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground">{registration?.email}</p>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(registration?.verificationStatus)}`}>
                      {registration?.verificationStatus}
                    </span>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(registration?.approvalStatus)}`}>
                      {registration?.approvalStatus}
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-2 mb-3">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Icon name="Tag" size={14} />
                    <span className="line-clamp-1">{registration?.registrationType}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Icon name="Calendar" size={14} />
                    <span>{formatDate(registration?.submissionDate)}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground col-span-2">
                    <Icon name="FileText" size={14} />
                    <span>{registration?.documents?.length} documents</span>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onViewDetails(registration)}
                    iconName="Eye"
                    iconPosition="left"
                  >
                    View Details
                  </Button>
                  {registration?.approvalStatus === 'Pending' && (
                    <>
                      <Button
                        variant="success"
                        size="sm"
                        onClick={() => onApprove(registration?.id)}
                        iconName="CheckCircle"
                        iconPosition="left"
                      >
                        Approve
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onReject(registration?.id)}
                        iconName="XCircle"
                        iconPosition="left"
                      >
                        Reject
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onRequestInfo(registration?.id)}
                        iconName="MessageSquare"
                        iconPosition="left"
                      >
                        Request Info
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="border-b border-border">
            <th className="text-left p-3">
              <Checkbox
                id="select-all"
                checked={selectedRegistrations?.length === registrations?.length}
                onChange={onSelectAll}
              />
            </th>
            <th className="text-left p-3 text-sm font-semibold text-foreground">Applicant</th>
            <th className="text-left p-3 text-sm font-semibold text-foreground">Type</th>
            <th className="text-left p-3 text-sm font-semibold text-foreground">Submission Date</th>
            <th className="text-left p-3 text-sm font-semibold text-foreground">Verification</th>
            <th className="text-left p-3 text-sm font-semibold text-foreground">Approval</th>
            <th className="text-left p-3 text-sm font-semibold text-foreground">Actions</th>
          </tr>
        </thead>
        <tbody>
          {registrations?.map((registration) => (
            <tr
              key={registration?.id}
              className={`border-b border-border hover:bg-muted/50 transition-smooth ${
                selectedRegistrations?.includes(registration?.id) ? 'bg-primary/5' : ''
              }`}
            >
              <td className="p-3">
                <Checkbox
                  id={`reg-${registration?.id}`}
                  checked={selectedRegistrations?.includes(registration?.id)}
                  onChange={() => onSelectRegistration(registration?.id)}
                />
              </td>
              <td className="p-3">
                <div className="flex items-center gap-2">
                  {registration?.priority === 'high' && (
                    <Icon name="AlertCircle" size={16} className={getPriorityColor(registration?.priority)} />
                  )}
                  <div>
                    <p className="text-sm font-medium text-card-foreground">{registration?.applicantName}</p>
                    <p className="text-xs text-muted-foreground">{registration?.email}</p>
                  </div>
                </div>
              </td>
              <td className="p-3">
                <div className="flex items-center gap-2">
                  <Icon name={getTypeIcon(registration?.registrationType)} size={16} color="var(--color-primary)" />
                  <span className="text-sm text-card-foreground">{registration?.registrationType}</span>
                </div>
              </td>
              <td className="p-3">
                <span className="text-sm text-card-foreground">{formatDate(registration?.submissionDate)}</span>
              </td>
              <td className="p-3">
                <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(registration?.verificationStatus)}`}>
                  {registration?.verificationStatus}
                </span>
              </td>
              <td className="p-3">
                <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(registration?.approvalStatus)}`}>
                  {registration?.approvalStatus}
                </span>
              </td>
              <td className="p-3">
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => onViewDetails(registration)}
                  >
                    <Icon name="Eye" size={16} />
                  </Button>
                  {registration?.approvalStatus === 'Pending' && (
                    <>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => onApprove(registration?.id)}
                      >
                        <Icon name="CheckCircle" size={16} color="var(--color-success)" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => onReject(registration?.id)}
                      >
                        <Icon name="XCircle" size={16} color="var(--color-error)" />
                      </Button>
                    </>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default RegistrationTable;